// Piezas visuales compartidas por la tabla y la ficha de Tiendas: medalla, chips y alertas.
import type { ReactNode } from 'react';
import { Medal, Moon, Target, TrendingDown } from 'lucide-react';
import { cn } from '@/lib/cn';
import type { StoreAlerts } from '../lib/storesTable';

const MEDAL_CLASS: Record<number, string> = {
    1: 'text-[#c9a227]',
    2: 'text-[#94a3b8]',
    3: 'text-[#b06a2b]',
};

/** Medalla oro, plata o bronce para los tres primeros lugares por colocación. */
export function RankMedal({ rank, className }: { rank: number | undefined; className?: string }) {
    if (rank === undefined || rank > 3) return null;
    return (
        <Medal
            className={cn('inline-block size-3.5 flex-none', MEDAL_CLASS[rank], className)}
            aria-label={`Lugar ${String(rank)} en colocación`}
        />
    );
}

export function AdminChip({ children = 'Admin' }: { children?: ReactNode }) {
    return (
        <span className="inline-flex items-center rounded border border-info-soft-border bg-info-soft px-1.5 py-px text-[9px] font-bold uppercase tracking-[0.04em] text-info">
            {children}
        </span>
    );
}

export function NoAdminChip() {
    return (
        <span
            className="inline-flex items-center whitespace-nowrap rounded border border-warning/30 bg-warning-soft px-1 py-px text-[8.5px] font-bold uppercase tracking-[0.02em] text-warning"
            title="La tienda no tiene encargado asignado en el padrón"
        >
            sin admin
        </span>
    );
}

function AlertBadge({
    tone,
    title,
    children,
}: {
    tone: 'success' | 'muted' | 'warning';
    title: string;
    children: ReactNode;
}) {
    return (
        <span
            title={title}
            aria-label={title}
            role="img"
            className={cn(
                'mr-0.5 inline-flex size-[18px] cursor-help items-center justify-center rounded',
                tone === 'success' && 'bg-success-soft text-success',
                tone === 'muted' && 'bg-muted text-muted-foreground',
                tone === 'warning' && 'bg-warning-soft text-warning',
            )}
        >
            {children}
        </span>
    );
}

export function noSaleText(noSale: NonNullable<StoreAlerts['noSale']>): string {
    return noSale.days === null
        ? 'Sin ventas en el periodo'
        : `${String(noSale.days)} días sin venta`;
}

export function AlertBadges({ alerts }: { alerts: StoreAlerts }) {
    const badges: ReactNode[] = [];
    if (alerts.topNetwork)
        badges.push(
            <AlertBadge key="top" tone="success" title="Top 10% de la red por colocación">
                <Target className="size-3" aria-hidden />
            </AlertBadge>,
        );
    if (alerts.noSale !== null)
        badges.push(
            <AlertBadge key="noSale" tone="muted" title={noSaleText(alerts.noSale)}>
                <Moon className="size-3" aria-hidden />
            </AlertBadge>,
        );
    if (alerts.drop)
        badges.push(
            <AlertBadge
                key="drop"
                tone="warning"
                title="Colocación cayó más del 20% vs semana anterior"
            >
                <TrendingDown className="size-3" aria-hidden />
            </AlertBadge>,
        );
    if (badges.length === 0)
        return <span className="text-[14px] leading-none text-muted-foreground">·</span>;
    return <span className="flex">{badges}</span>;
}

/** Barra de cumplimiento en 4 colores del mock; sin meta pinta un guion, nunca una barra en cero. */
export function GoalBar({
    progress,
    title,
    inline = false,
}: {
    progress: number | null;
    title?: string;
    /** Barra y porcentaje en una línea, para filas de tabla. */
    inline?: boolean;
}) {
    if (progress === null) {
        return (
            <span
                className="text-[10.5px] text-muted-foreground"
                title="Sin meta para este periodo"
            >
                —
            </span>
        );
    }
    const pct = Math.round(progress * 100);
    return (
        <div
            className={inline ? 'flex items-center gap-1' : 'flex flex-col gap-[3px]'}
            title={title}
        >
            <div className={cn('h-1.5 overflow-hidden rounded-full bg-muted', inline && 'flex-1')}>
                <div
                    className={cn(
                        'h-full rounded-full',
                        pct >= 100
                            ? 'bg-success'
                            : pct >= 80
                              ? 'bg-warning'
                              : pct >= 50
                                ? 'bg-[hsl(35_90%_55%)]'
                                : 'bg-danger',
                    )}
                    style={{ width: `${String(Math.min(pct, 100))}%` }}
                />
            </div>
            <span className="text-[10px] font-semibold tabular-nums">{pct}%</span>
        </div>
    );
}

/** `▲ 12%` verde, `▼ 8%` rojo, `—` si cambia menos de 2%, `nuevo` si la semana anterior fue 0. */
export function WeekDelta({ pct, isNew }: { pct: number | null; isNew: boolean }) {
    if (isNew) {
        return (
            <span
                className="text-[10px] font-semibold italic text-info"
                title="Sin ventas la semana anterior"
            >
                nuevo
            </span>
        );
    }
    if (pct === null) return <span className="text-muted-foreground">—</span>;
    if (Math.abs(pct) < 2) {
        return (
            <span className="text-muted-foreground" title="Sin cambio vs semana anterior">
                —
            </span>
        );
    }
    const rounded = Math.round(pct);
    return rounded > 0 ? (
        <span className="text-[10px] font-bold text-success">▲ {rounded}%</span>
    ) : (
        <span className="text-[10px] font-bold text-danger">▼ {Math.abs(rounded)}%</span>
    );
}
